/**
 * Code Mode for Bear's lead: the operations its browser `eval` reaches as
 * `tools.<namespace>.<tool>(input)`. `bear` holds the lead's own operations,
 * derived from the same tool definitions the turn registers, and each jig
 * with tools is a namespace of its own. A tool the browser runs (the sandbox
 * and checkpoint tools, every jig tool) answers a call with its page, which
 * the studio page runs; the rest run here. Core file calls go to the same
 * bridge host the top-level file tools use, so version gates are shared.
 */
import { jsonSchema, type Tool } from "ai";
import type { JSONSchema7 } from "json-schema";
import {
  createAgentToolsApi,
  type NamespaceTool,
  type ToolNamespace,
} from "playground/agent-tools/catalog";
import type {
  AgentToolsApi,
  CoreToolsApi,
} from "playground/agent-tools/contract";
import { invokeAgentTools } from "playground/agent-tools/dispatch";
import {
  appStructuredOutput,
  mcpData,
  mcpError,
} from "playground/apps/tool-mcp";
import { appNamespace, type AppToolSpec } from "playground/apps/tool-routing";
import type { McpApi } from "playground/mcp/contract";
import { invariant } from "ts-invariant";
import { z } from "zod";

/** The page answer's App for the tools the studio page runs itself. */
export const BEAR_PAGE = "bear";

/** Bear has no MCP connections. */
const NO_MCP: McpApi = {
  servers: async () => [],
  tools: async (server) => {
    throw new Error(`Unknown namespace ${server}.`);
  },
  search: async () => [],
  describe: async (selector) => {
    throw new Error(`Unknown tool ${selector}.`);
  },
  call: async (selector) => {
    throw new Error(`Unknown tool ${selector}.`);
  },
};

/**
 * An operation as an MCP tool: one the host executes resolves to its data and
 * fails by throwing; one without `execute` is the browser's to run.
 */
function operationAsMcp(name: string, op: Tool): NamespaceTool {
  invariant(
    typeof op.description === "string",
    `Every Bear operation describes itself in text (${name}).`,
  );
  const schemas = {
    description: op.description,
    inputSchema: op.inputSchema,
    outputSchema: op.outputSchema,
  };
  const execute = op.execute;
  if (!execute) return { ...schemas, page: { appId: BEAR_PAGE, tool: name } };
  return {
    ...schemas,
    execute: async (input) => {
      try {
        return mcpData(
          await execute(input, {
            toolCallId: `eval:${crypto.randomUUID()}`,
            messages: [],
            context: {},
          }),
        );
      } catch (error) {
        return mcpError(error instanceof Error ? error.message : String(error));
      }
    },
  };
}

/** A jig tool: page-only, run by the studio in the jig's page. */
function jigToolAsMcp({ appId, tool }: AppToolSpec): NamespaceTool {
  const output = appStructuredOutput(tool.outputSchema);
  return {
    description: tool.description,
    inputSchema: jsonSchema(tool.inputSchema as JSONSchema7),
    ...(output && { outputSchema: jsonSchema(output as JSONSchema7) }),
    page: { appId, tool: tool.name },
  };
}

export function bearAgentToolsApi(
  core: CoreToolsApi,
  operations: Record<string, Tool>,
  jigs: AppToolSpec[],
): AgentToolsApi {
  const namespaces: Record<string, ToolNamespace> = {
    bear: {
      description:
        "Model diagnostics, sketch edits, measurement arithmetic, building and inspecting the model, the user's screen, checkpoints, and task action attachments.",
      tools: Object.fromEntries(
        Object.entries(operations).map(([name, op]) => [
          name,
          operationAsMcp(name, op),
        ]),
      ),
    },
  };
  for (const spec of jigs) {
    const namespace = (namespaces[appNamespace(spec.appId)] ??= {
      description: `Tools published by the ${spec.appId} jig.`,
      tools: {},
    });
    namespace.tools[spec.tool.name] = jigToolAsMcp(spec);
  }
  return createAgentToolsApi(core, namespaces, NO_MCP);
}

const agentToolsRequest = z.object({
  toolCallId: z.string(),
  method: z.string(),
  args: z.array(z.unknown()),
});

/**
 * A browser eval's tool call. It is served only while `live` says the eval
 * that makes it is still parked for the browser, so a script's tools end
 * with its call and a stale page can't act for the agent.
 */
export async function serveAgentTools(
  request: Request,
  live: (toolCallId: string) => Promise<boolean>,
  api: () => Promise<AgentToolsApi>,
): Promise<Response> {
  const body = agentToolsRequest.parse(await request.json());
  if (!(await live(body.toolCallId)))
    return Response.json(
      { error: "Agent tools are only available while the agent's eval runs." },
      { status: 409 },
    );
  try {
    return Response.json({
      result: await invokeAgentTools(await api(), body.method, body.args),
    });
  } catch (error) {
    return Response.json(
      { error: error instanceof Error ? error.message : String(error) },
      { status: 400 },
    );
  }
}
